import { useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import type { Stripe, PaymentRequest } from '@stripe/stripe-js';
import { Elements, PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import { createSetupIntent, savePaymentMethod } from '../api/client';

import { getStripeKey } from '../config/env';
const STRIPE_PUBLISHABLE_KEY = getStripeKey();
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

interface AddApplePayButtonProps {
  onSaved: () => void;
  onError?: (message: string) => void;
}

function ApplePayInner({ onSaved, onError }: AddApplePayButtonProps) {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  // The PaymentRequest "amount" is required by Stripe even in a setup-only
  // flow; it's never charged because we hand the resulting PaymentMethod to
  // confirmCardSetup, not confirmCardPayment.
  const setupAmountCents = 0;

  useEffect(() => {
    if (!stripe) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label: 'Save Apple Pay', amount: setupAmountCents },
      requestPayerName: true,
      requestPayerEmail: false,
      disableWallets: ['link', 'googlePay', 'browserCard'],
    });

    pr.canMakePayment().then((result) => {
      if (result?.applePay) {
        setPaymentRequest(pr);
      }
    });

    pr.on('paymentmethod', async (ev) => {
      try {
        const { clientSecret } = await createSetupIntent();
        const { error: confirmError, setupIntent } = await stripeConfirmSetup(
          stripe,
          clientSecret,
          ev.paymentMethod.id,
        );

        if (confirmError) {
          ev.complete('fail');
          onError?.(confirmError.message || 'Failed to save Apple Pay');
          return;
        }

        ev.complete('success');

        if (setupIntent?.payment_method) {
          await savePaymentMethod(setupIntent.payment_method as string);
          onSaved();
        }
      } catch (err) {
        ev.complete('fail');
        onError?.(err instanceof Error ? err.message : 'Failed to save Apple Pay');
      }
    });
  }, [stripe, onSaved, onError]);

  if (!paymentRequest) return null;

  return (
    <PaymentRequestButtonElement
      options={{
        paymentRequest,
        style: {
          paymentRequestButton: {
            type: 'default',
            theme: 'dark',
            height: '48px',
          },
        },
      }}
    />
  );
}

async function stripeConfirmSetup(
  stripe: Stripe,
  clientSecret: string,
  paymentMethodId: string,
) {
  return stripe.confirmCardSetup(
    clientSecret,
    { payment_method: paymentMethodId },
    { handleActions: false },
  );
}

export function AddApplePayButton({ onSaved, onError }: AddApplePayButtonProps) {
  const ref = useRef(stripePromise);
  if (!ref.current) return null;
  return (
    <Elements stripe={ref.current}>
      <ApplePayInner onSaved={onSaved} onError={onError} />
    </Elements>
  );
}
